import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import isNumber from 'hs-lodash/isNumber';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import { FormattedIntlCurrency } from 'I18n/components/FormattedIntlCurrency';
import FormattedPhoneNumber from 'I18n/components/FormattedPhoneNumber';
import FormattedDateTime from 'I18n/components/FormattedDateTime';
import { DialogBody } from 'foundations-components/transitional/components/DialogBody';
import { Link } from 'foundations-components/transitional/components/Link';
import { Section } from 'foundations-components/transitional/components/Section';
import { Text } from 'foundations-components/components/Text';
import { Flex } from 'foundations-components/components/Flex';
import AchFuturePaymentsMessage from '../FutureCharges/AchFuturePaymentsMessage';
import { getIsAnyRecurring } from '../../utils/recurring';
import { useSubtotals } from '../../context/SubtotalsProvider';
import { AT_CHECKOUT } from '../../constants/LineItems';
import { getIsZeroCheckoutValid } from '../../utils/validations';
import { isRelativeStartDateCheckoutLineItem } from '../../types/utils/typeguards';
import { usePaymentFormContext } from '../../context/PaymentFormContext';
import { getBuyerOverridesPartialPaymentAmount } from '../../state/BuyerOverrides/selectors';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const ACH_TERMS_PAGE = 'https://legal.hubspot.com/debit-authorization';
const AchModalTerms = ({
  checkoutSession,
  selectedOptionalLineItems
}) => {
  var _checkoutSession$user;
  const {
    formState
  } = usePaymentFormContext();
  const partialPaymentAmount = useSelector(getBuyerOverridesPartialPaymentAmount);
  const {
    paymentAmount
  } = useSubtotals();
  const accountHolderName = formState.getIn(['bankInfo', 'accountHolderName']);
  const companyName = ((_checkoutSession$user = checkoutSession.userDefinedBranding) === null || _checkoutSession$user === void 0 ? void 0 : _checkoutSession$user.companyName) || null;
  const currencyCode = checkoutSession.currencyCode;
  const isAnyRecurring = getIsAnyRecurring(checkoutSession.lineItems, selectedOptionalLineItems) || !!checkoutSession.futureContractLineItems && getIsAnyRecurring(checkoutSession.futureContractLineItems);
  const email = checkoutSession.companyDetails && checkoutSession.companyDetails.supportEmail;
  const phone = checkoutSession.companyDetails && checkoutSession.companyDetails.supportPhone;
  const {
    recurringSubtotals = []
  } = useSubtotals();
  const hasRecurringSubtotals = recurringSubtotals.length > 0;
  const isOneTimeAtCheckoutRecurring = hasRecurringSubtotals && recurringSubtotals.every(lineItem => lineItem.recurringBillingTerms.initialPayment.type === AT_CHECKOUT) && recurringSubtotals.every(lineItem => lineItem.recurringBillingTerms.numPayments === 1);
  const isZeroCheckout = getIsZeroCheckoutValid(checkoutSession.subtotalsPublicResponse.paymentAmount, checkoutSession.lineItems);
  const paymentDateType = isZeroCheckout && hasRecurringSubtotals && isRelativeStartDateCheckoutLineItem(recurringSubtotals[0]) ? 'RelativeDelay' : 'StartDate';
  const {
    futureStartDate = null,
    relativeDelay = null
  } = isZeroCheckout && hasRecurringSubtotals ? recurringSubtotals[0].recurringBillingTerms.initialPayment : {};
  const {
    delayLength = null,
    delayUnit = null
  } = isZeroCheckout && hasRecurringSubtotals && isRelativeStartDateCheckoutLineItem(recurringSubtotals[0]) && relativeDelay ? relativeDelay : {};
  const supportMessage = useMemo(() => {
    if (email && phone) {
      return /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: "payments.paymentElementAuthModal.paymentAuthDescription3WithEmailAndPhone_jsx",
        options: {
          companyName,
          email,
          phone
        },
        elements: {
          Phone: FormattedPhoneNumber
        }
      });
    } else if (phone) {
      return /*#__PURE__*/_jsx(FormattedJSXMessage, {
        message: "payments.paymentElementAuthModal.paymentAuthDescription3WithPhone_jsx",
        options: {
          companyName,
          phone
        },
        elements: {
          Phone: FormattedPhoneNumber
        }
      });
    } else if (email) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "payments.paymentElementAuthModal.paymentAuthDescription3WithEmail",
        options: {
          companyName,
          email
        }
      });
    } else if (companyName) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "payments.paymentElementAuthModal.paymentAuthDescription3WithCompanyName",
        options: {
          companyName
        }
      });
    }
    // we shouldn't ever really get here but added just in case
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "payments.paymentElementAuthModal.paymentAuthDescription3WithoutSupportInfo"
    });
  }, [companyName, email, phone]);
  const amountMessageKey = (() => {
    if (isAnyRecurring && !isOneTimeAtCheckoutRecurring) {
      return 'payments.paymentElementAuthModal.firstPayment';
    } else if (isNumber(partialPaymentAmount)) {
      return 'payments.paymentElementAuthModal.paymentAmount';
    } else {
      return 'payments.paymentElementAuthModal.total';
    }
  })();
  return /*#__PURE__*/_jsxs(DialogBody, {
    children: [/*#__PURE__*/_jsx(Section, {
      children: /*#__PURE__*/_jsx(Text, {
        as: "p",
        typography: "detail-200",
        "data-test-id": "ach-authorization-modal-terms",
        children: /*#__PURE__*/_jsx(FormattedJSXMessage, {
          message: "payments.paymentElementAuthModal.paymentAuthDescription1_jsx",
          options: {
            href: ACH_TERMS_PAGE,
            accountHolderName,
            companyName
          },
          elements: {
            Link
          }
        })
      })
    }), /*#__PURE__*/_jsx(Section, {
      children: /*#__PURE__*/_jsx(Text, {
        as: "p",
        typography: "detail-200",
        "data-test-id": "ach-authorization-modal-payment-description",
        children: isAnyRecurring && !isOneTimeAtCheckoutRecurring ? /*#__PURE__*/_jsx(FormattedJSXMessage, {
          message: `payments.paymentElementAuthModal.paymentAuthDescription2WithRecurring${paymentDateType}_jsx`,
          options: {
            startDate: /*#__PURE__*/_jsx(FormattedDateTime, {
              value: futureStartDate || Date.now(),
              format: "LL",
              type: "utc"
            }),
            delayLength,
            delayUnit: /*#__PURE__*/_jsx(FormattedMessage, {
              message: `payments.relativeStartDateDelayUnits.${delayUnit}`,
              options: {
                count: delayLength
              }
            })
          }
        }) : /*#__PURE__*/_jsx(FormattedJSXMessage, {
          message: "payments.paymentElementAuthModal.paymentAuthDescription2_jsx",
          options: {
            todayDate: /*#__PURE__*/_jsx(FormattedDateTime, {
              value: Date.now(),
              format: "LL",
              type: "utc"
            })
          }
        })
      })
    }), /*#__PURE__*/_jsx(Section, {
      height: "200",
      children: /*#__PURE__*/_jsxs(Flex, {
        alignItems: "baseline",
        justifyContent: "space-between",
        children: [/*#__PURE__*/_jsx(Text, {
          typography: "heading-25",
          "data-test-id": "ach-authorization-modal-amount-text",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: amountMessageKey
          })
        }), /*#__PURE__*/_jsx(Text, {
          as: "small",
          textAlign: "right",
          children: /*#__PURE__*/_jsx("span", {
            "data-test-id": "ach-authorization-modal-payment-amount",
            children: /*#__PURE__*/_jsx(FormattedIntlCurrency, {
              value: paymentAmount,
              currencyCode: currencyCode
            })
          })
        })]
      })
    }), /*#__PURE__*/_jsx(Section, {
      children: isAnyRecurring && !isOneTimeAtCheckoutRecurring && /*#__PURE__*/_jsxs(Flex, {
        alignItems: "baseline",
        justifyContent: "space-between",
        children: [/*#__PURE__*/_jsx(Text, {
          typography: "heading-25",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "payments.paymentElementAuthModal.futurePayments"
          })
        }), /*#__PURE__*/_jsx(Text, {
          as: "small",
          textAlign: "right",
          children: /*#__PURE__*/_jsx("span", {
            "data-test-id": "ach-authorization-modal-future-payments",
            children: /*#__PURE__*/_jsx(AchFuturePaymentsMessage, {
              currencyCode: currencyCode
            })
          })
        })]
      })
    }), /*#__PURE__*/_jsx(Section, {
      children: /*#__PURE__*/_jsx(Text, {
        as: "p",
        typography: "detail-200",
        "data-test-id": "ach-authorization-modal-support-info",
        children: supportMessage
      })
    })]
  });
};
export default AchModalTerms;